import React, { useContext, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Card, CardContent, Typography, IconButton, Checkbox } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Controller, useForm } from 'react-hook-form';
import { clientStaffEditSchema } from '../../../validation/users';
import {
  gray40,
  gray80,
  gray100,
  orange50,
  primaryButtonBackgroundColor,
  red50,
  white,
} from '../../../constants/colors';
import { sizeBelowTenMb, textMb, titleMb, w100 } from '../../../constants/location';
import { EMAIL_INPUT_PLACEHOLDER } from '../../../constants/placeholders';
import { GROUP_EMPTY_MESSAGE } from '../../../constants/labels';

import CustomImageUploader from '../form/CustomImageUploader';
import CustomLoading from '../form/CustomLoading';
import CustomRadioButton from '../form/CustomRadioButton';
import CustomSnackbar from '../form/CustomSnackbar';
import CustomTextfield from '../form/CustomTextfield';
import { doc, getDoc } from 'firebase/firestore';
import {
  editStaffUser,
  updateUserMonthlyOcrLimit,
} from '../../../api/firebase/firestore/user/update';
import { common, ValidationError, adminCountError } from '../../../modules/error';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import * as Sentry from '@sentry/react';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import { usersRef, getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import { getCompany } from '../../../api/firebase/firestore/company/read';
import { setUserGroups } from '../../../api/firebase/firestore/group/member';
import { useCompanyGroups } from '../../../hooks/useCompanyGroups';
import { AuthContext } from '../../../contexts/auth';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { companyPlan as companyPlanEnum, userRole } from '../../../constants/enum';

const styles = {
  cardSize: {
    '@media screen and (max-width:480px)': {
      width: 343,
    },
    boxShadow: 0,
    marginLeft: '16px',
    marginRight: '16px',
    width: 480,
  },
  cardContentPadding: {
    '&:last-child': {
      '@media screen and (max-width:480px)': {
        paddingBottom: '20px',
      },
      paddingBottom: '24px',
    },
    '@media screen and (max-width:480px)': {
      padding: '20px 16px',
    },
    padding: '24px 40px',
  },
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0,
    },
    marginBottom: '16px',
  },
  required: {
    color: red50,
    marginLeft: '10px',
  },
  snackbar: {
    '@media screen and (max-width:960px)': {
      bottom: '24px',
      left: '20px',
      position: 'fixed',
      right: '20px',
    },
    bottom: '24px',
    display: 'block',
    left: '24px',
    position: 'absolute',
  },
  submitButton: {
    height: '42px',
    color: primaryButtonBackgroundColor,
  },
  textMb,
  textSmall: {
    fontSize: '11.5px',
  },
  titleMb,
  w100,
  // カードをflexカラム化し、フッターを常時表示する（#2804）
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 64px)',
    overflow: 'hidden',
  },
  cardContentScroll: {
    flex: 1,
    overflowY: 'auto',
  },
  cardFooter: {
    display: 'flex',
    padding: '16px 40px',
    borderTop: `1px solid ${gray40}`,
    backgroundColor: white,
    flexShrink: 0,
    '@media screen and (max-width:480px)': {
      padding: '16px',
    },
  },
  groupLabel: {
    fontSize: '14px',
    color: gray80,
    marginBottom: '8px',
    textAlign: 'left',
  },
  groupList: {
    maxHeight: '200px',
    overflowY: 'auto',
  },
  groupRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    // チェックボックスをラベル「所属グループ」の左端に揃えるため左パディングは持たせない（#2804）
    padding: '4px 0',
    cursor: 'pointer',
  },
  groupName: {
    fontSize: '14px',
    color: gray100,
  },
  groupEmpty: {
    fontSize: '12px',
    color: gray80,
    padding: '16px',
    textAlign: 'center',
  },
};

export interface EditStaffType {
  email?: string | null | undefined;
  name: string;
  department: string;
  role: boolean;
  icon?: File | undefined;
}

const StaffEditCard: React.FC = () => {
  const navigate = useNavigate();
  const { user, setCurrentUser } = useContext(AuthContext);
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [isFreePlan, setIsFreePlan] = useState<boolean>(false);
  const [originalIsAdmin, setOriginalIsAdmin] = useState<boolean>(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  // 月間OCR上限（#2804 Phase4）。空=無制限（未設定）、数値=上限ページ数
  const [monthlyOcrLimit, setMonthlyOcrLimit] = useState<string>('');
  const location = useLocation();
  const [uid] = useState<string>((location.state as { id: string })?.id);
  const [isAdmin] = useState<boolean>((location.state as { isAdmin: boolean })?.isAdmin);
  const { companyId } = useCompanyId();
  // 所属グループ選択用のグループ一覧（共通フック #2804）
  const { groups: availableGroups } = useCompanyGroups(companyId);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<EditStaffType>({
    defaultValues: {
      department: '',
      name: '',
      role: false,
    },
    resolver: yupResolver(clientStaffEditSchema),
  });

  useEffect(() => {
    void (async (): Promise<void> => {
      setLoading(true);
      try {
        if (uid) {
          const userRef = doc(usersRef, uid);
          const userSnap = await getDoc(userRef);
          const user = userSnap.data();
          setPreviewIcon(user?.icon_url);

          if (user) {
            const isUserAdmin = Boolean(Number(user?.role) === 1);
            setOriginalIsAdmin(isUserAdmin);
            setSelectedGroupIds((user?.group_ids as string[] | undefined) ?? []);
            // 月間OCR上限（#2804 Phase4）: number(>0)なら表示、それ以外は空（無制限）
            const rawLimit = user?.usage_limit;
            setMonthlyOcrLimit(
              typeof rawLimit === 'number' && rawLimit > 0 ? String(rawLimit) : '',
            );
            reset({
              // 企業招待では部署名が未入力（null）で登録される場合がある（#3071）
              department: (user?.department as string | null) ?? '',
              email: user?.email as string,
              name: user?.name as string,
              role: isUserAdmin,
            });
          }
        }

        // 企業情報を取得してフリープランかどうかを判定
        if (companyId) {
          const companyData = await getCompany(companyId);
          if (companyData) {
            setIsFreePlan(companyData.plan === companyPlanEnum.free);
          }
        }
      } catch (e) {
        // ログアウト直後は依存（companyId）の変化で再実行され permission-denied になるため無視する
        if (!(e instanceof FirebaseError && e.code === 'permission-denied')) {
          Sentry.captureException(e);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, reset, uid]);

  const boolToString = (isAdmin: boolean): string => (isAdmin ? '1' : '0');

  const toggleGroup = (gid: string): void => {
    setSelectedGroupIds(prev =>
      prev.includes(gid) ? prev.filter(id => id !== gid) : [...prev, gid],
    );
  };

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('name') === undefined ||
        '' ||
        watch('email') === undefined ||
        '' ||
        watch('role') === undefined ||
        Boolean(errors.name) ||
        Boolean(errors.icon) ||
        Boolean(errors.department) ||
        Boolean(iconSizeError)
      );
    }
    return false;
  };

  const [previewIcon, setPreviewIcon] = useState('');
  const [s3FileIcon, setS3FileIcon] = useState<FileList | null>(null);
  const [iconSizeError, setIconSizeError] = useState<string | null>(null);

  useEffect(() => {
    if (s3FileIcon && s3FileIcon.length > 0) {
      sizeBelowTenMb(s3FileIcon[0].size)
        ? setIconSizeError('画像は10MBまでしか登録できません')
        : setIconSizeError(null);
    } else {
      setIconSizeError(null);
    }
  }, [s3FileIcon]);

  const changeUploadIconFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file && file.length > 0) {
      const blob = new Blob([file[0]]);
      setPreviewIcon(window.URL.createObjectURL(blob));
      setS3FileIcon(file);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      setFirstSubmit(true);

      // 管理者→メンバーへの変更時、管理者が0人にならないかチェック
      if (companyId && originalIsAdmin && watch('role') === false) {
        const companyUsers = await getUsersByCompanyId(companyId);
        const adminCount = companyUsers.filter(u => u.role === userRole.companyAdmin).length;
        if (adminCount <= 1) {
          throw new ValidationError(adminCountError);
        }
      }

      // 月間OCR上限の入力チェック（#2804 Phase4）
      const hasOcrLimit = monthlyOcrLimit.trim() !== '';
      const parsedOcrLimit = hasOcrLimit ? Number(monthlyOcrLimit) : null;
      if (hasOcrLimit && (!Number.isInteger(parsedOcrLimit) || (parsedOcrLimit ?? 0) <= 0)) {
        throw new ValidationError('月間OCR上限は1以上の整数を入力してください。');
      }

      const { iconUrl } = await editStaffUser(uid, watch, s3FileIcon);

      // 所属グループの変更を反映（#2804）
      if (companyId && user) {
        await setUserGroups(companyId, uid, selectedGroupIds, user.uid);
      }

      // 月間OCR上限の変更を反映（#2804 Phase4）。管理者のみ設定可
      if (boolToString(isAdmin) === '1' && user) {
        await updateUserMonthlyOcrLimit(uid, parsedOcrLimit, user.uid);
      }

      // ログインユーザー自身を編集した場合、AuthContextを更新
      if (user && uid === user.uid) {
        setCurrentUser({
          ...user,
          name: watch('name') as string,
          icon: iconUrl ?? user.icon,
          group_ids: selectedGroupIds,
        });
      }

      setOpen(true);
      setLoading(false);
      navigate('/staff/edit/complete');
    } catch (error) {
      let errorText: string;
      if (error instanceof FirebaseError) {
        errorText = getErrorMessageFromAuthErrorCode(error.code);
      } else if (error instanceof AxiosError && error.response) {
        errorText = error.response.data.message;
      } else if (error instanceof Error) {
        errorText = error.message;
      } else {
        errorText = common;
      }
      setLoading(false);
      setErrorText(errorText);
      setOpen(true);
    } finally {
      setFirstSubmit(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Box>
          <Card sx={[styles.cardSize, styles.cardColumn]}>
            <CardContent sx={[styles.cardContentPadding, styles.cardContentScroll]}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginBottom: '16px',
                }}
              >
                <IconButton
                  onClick={(): void => navigate('/staff')}
                  size="small"
                  disabled={loading}
                  aria-label="戻る"
                  sx={{ marginLeft: '-8px' }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  sx={[styles.titleMb, { color: gray100, textAlign: 'left', marginBottom: 0 }]}
                  component="div"
                >
                  担当者編集
                </Typography>
              </Box>
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomImageUploader
                    {...field}
                    type="icon"
                    preview={previewIcon}
                    error={Boolean(errors.icon)}
                    otherError={iconSizeError}
                    helperText={errors.icon?.message}
                    formMb="16px"
                    onChange={(value: React.ChangeEvent<HTMLInputElement>): void => {
                      field.onChange(value);
                      changeUploadIconFile(value);
                    }}
                  />
                )}
                control={control}
                name="icon"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText="メールアドレス"
                    required
                    requiredText="必須"
                    placeholder={EMAIL_INPUT_PLACEHOLDER}
                    disabled
                    formMb="16px"
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    labelText="担当者名"
                    required
                    requiredText="必須"
                    formMb="16px"
                  />
                )}
                control={control}
                name="name"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText="部署"
                    formMb="16px"
                  />
                )}
                control={control}
                name="department"
              />
              {/* 月間OCR上限（#2804 Phase4）: 管理者のみ設定可。部署の直下に配置 */}
              {boolToString(isAdmin) === '1' && (
                <Box sx={{ textAlign: 'left', marginBottom: '16px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', mb: '8px' }}>
                    <Typography sx={{ ...styles.groupLabel, marginBottom: 0 }}>
                      月間OCR上限（ページ数）
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: gray80 }}>
                      空欄の場合は無制限になります。
                    </Typography>
                  </Box>
                  <CustomTextfield
                    type="number"
                    value={monthlyOcrLimit}
                    onChange={(e): void =>
                      setMonthlyOcrLimit(e.target.value.replace(/[^0-9]/g, ''))
                    }
                    placeholder="例: 500"
                    formMb="0px"
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Box>
              )}
              {boolToString(isAdmin) === '1' && (
                <Controller
                  render={({ field }): React.ReactElement => (
                    <CustomRadioButton
                      value={field.value ? '1' : '0'}
                      onChange={(e): void => field.onChange(e.target.value === '1')}
                      error={Boolean(errors.role)}
                      helperText={errors.role?.message}
                      labelText="権限"
                      required
                      requiredText="必須"
                      firstOption="管理者"
                      secondOption="メンバー"
                      optionFontSize="14px"
                      inlineDescription
                      firstOptionDescription="担当者の閲覧・招待・編集ができます。"
                      secondOptionDescription="担当者の閲覧ができます。"
                      firstValue={1}
                      secondValue={0}
                      disabled={isFreePlan}
                      attentionText={isFreePlan ? 'フリープランでは変更できません' : undefined}
                    />
                  )}
                  control={control}
                  name="role"
                />
              )}
              {boolToString(isAdmin) === '1' && (
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={styles.groupLabel}>所属グループ</Typography>
                  <Box sx={styles.groupList}>
                    {availableGroups.length === 0 ? (
                      <Typography sx={styles.groupEmpty}>{GROUP_EMPTY_MESSAGE}</Typography>
                    ) : (
                      availableGroups.map(g => (
                        <Box
                          key={g.id}
                          sx={styles.groupRow}
                          onClick={(): void => toggleGroup(g.id)}
                        >
                          <Checkbox
                            checked={selectedGroupIds.includes(g.id)}
                            // 内部パディング分を負マージンで打ち消し、チェック枠の左端をラベル左端に合わせる
                            sx={{
                              '&.Mui-checked': { color: orange50 },
                              padding: '4px',
                              marginLeft: '-4px',
                            }}
                          />
                          <Typography sx={styles.groupName}>{g.name}</Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
            <Box sx={styles.cardFooter}>
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={styles.submitButton}
                onClick={(): void => {
                  void (async (): Promise<void> => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      if (iconSizeError) return;
                      await onSubmit();
                    })();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 16, fontWeight: 700 }}>
                  変更する
                </Typography>
              </Button>
            </Box>
          </Card>
          <CustomSnackbar
            open={open}
            onClose={(): void => setOpen(false)}
            text={errorText}
            sx={styles.snackbar}
          />
        </Box>
      </Box>
    </>
  );
};

export default StaffEditCard;
